import React, { useState, useEffect, useRef } from 'react';
import { Check, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/shadcn-ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn-ui/dialog';
import { useParams } from 'react-router-dom';
import { appAxios } from '@/network/base/appAxios';
import { ArrowLeft } from 'lucide-react';

const PermitStepper = () => {
  const { projectId } = useParams();
  const [stepData, setStepData] = useState([]);
  const [noData, setNoData] = useState(false);
  const [noPermit, setNoPermit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [selectedStepName, setSelectedStepName] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const disableTimeoutRef = useRef(null);

  // Immediate scroll to top on component mount
  useEffect(() => {
    // Force scroll to top for window
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Find and scroll the parent scroll container (ProjectPage main element)
    const scrollParent = document.querySelector('main.overflow-y-auto');
    if (scrollParent) {
      scrollParent.scrollTop = 0;
    }

    // Also scroll after a short delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      const scrollParent = document.querySelector('main.overflow-y-auto');
      if (scrollParent) {
        scrollParent.scrollTop = 0;
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, []);

  const fetchPermitProgress = async () => {
    try {
      setIsRefreshing(true);
      const response = await appAxios.get(`/PermitProgress/v1.0/getPermitProgressByProjectId?projectId=${projectId}`);
      if (response.data?.status === 'Success' && response.data?.data) {
        if (response.data.data.length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
          setNoPermit(false);
        }
        // Sort steps: success steps first (by step_number), then error steps (by step_number)
        const successSteps = response.data.data
          .filter(step => step.script_status !== 'error')
          .sort((a, b) => a.step_number - b.step_number);
        const errorSteps = response.data.data
          .filter(step => step.script_status === 'error')
          .sort((a, b) => a.step_number - b.step_number);
        const sortedData = [...successSteps, ...errorSteps];
        setStepData(sortedData);
      } else {
        setNoPermit(true);
      }
    } catch (error) {
      setNoPermit(true);
      console.error('Error fetching permit progress:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPermitProgress();

    // Cleanup timeout on unmount
    return () => {
      if (disableTimeoutRef.current) {
        clearTimeout(disableTimeoutRef.current);
      }
    };
  }, [projectId]);

  const handleRefresh = () => {
    // Clear existing timeout if any
    if (disableTimeoutRef.current) {
      clearTimeout(disableTimeoutRef.current);
    }

    // Disable button for 15 seconds
    setIsButtonDisabled(true);

    // Fetch immediately
    fetchPermitProgress();

    // Re-enable button after 15 seconds
    disableTimeoutRef.current = setTimeout(() => {
      setIsButtonDisabled(false);
    }, 15000);
  };

  const handleViewScreenshot = (screenshot, stepName) => {
    setSelectedScreenshot(screenshot);
    setSelectedStepName(stepName);
  };

  const nextStep = () => setActiveStep(prev => Math.min(prev + 1, stepData.length - 1));
  const prevStep = () => setActiveStep(prev => Math.max(prev - 1, 0));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-white flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  const completedSteps = stepData.filter(s => s.status === 'completed').length;
  const totalSteps = stepData.length;
  const progressPercentage = (completedSteps / totalSteps) * 100;
  const hasErrors = stepData.some(s => s.script_status === 'error');
  const statusColor = hasErrors ? 'red' : 'yellow';

  if (noData || noPermit) {
    return (
      <div className="relative min-h-screen text-white flex items-center justify-center">
        {/* Back Button */}
        <div className="absolute top-4 left-4 z-50">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back</span>
          </button>
        </div>

        {/* Refresh Button */}
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={handleRefresh}
            disabled={isButtonDisabled}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${isButtonDisabled
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed border border-gray-700'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white border border-gray-600 hover:border-gray-500'
              }`}
          >
            <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
            <span className="text-sm font-medium">Refresh</span>
          </button>
        </div>

        {/* Centered Loading Progress Indicator */}
        {isRefreshing ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-lg text-gray-300 font-medium">Loading progress...</span>
          </div>
        ) : (
          <div className="text-gray-400">{noPermit ? 'No permit found' : 'Your Permit is added to queue'}</div>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-5xl mx-auto flex flex-col gap-8 text-foreground p-4 pt-16 min-h-screen">
      <div className="absolute top-4 left-4 z-50">
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition"
        >
          <ArrowLeft size={18} />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Refresh Button */}
      <div className="absolute top-4 right-4 z-50">
        <button
          onClick={handleRefresh}
          disabled={isButtonDisabled}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${isButtonDisabled
            ? 'bg-gray-800 text-gray-600 cursor-not-allowed border border-gray-700'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white border border-gray-600 hover:border-gray-500'
            }`}
        >
          <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* Centered Loading Progress Indicator */}
      {isRefreshing ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-4">
          <div
            className={`w-12 h-12 border-4 ${hasErrors ? 'border-red-500' : 'border-yellow-500'} border-t-transparent rounded-full animate-spin`}
          ></div>
          <span className="text-lg text-gray-300 font-medium">Loading progress...</span>
        </div>
      ) : (
        <>
          {/* Status / Progress Bar */}
          <div className="mb-8 w-full">
            <div className="flex  gap-2 mb-2 w-full justify-left items-center">
              <div className={`w-2.5 h-2.5 ${hasErrors ? 'bg-red-500' : 'bg-yellow-500'} rounded-full`}></div>
              <span className={`${hasErrors ? 'text-red-500' : 'text-yellow-500'} text-sm font-medium`}>
                {hasErrors ? 'Error Occurred' : 'Submitted to City'}
              </span>
            </div>
            <div className="relative h-2 bg-gray-800 rounded-full overflow-hidden mb-2">
              <div
                className={`absolute left-0 top-0 h-full ${hasErrors ? 'bg-red-500' : 'bg-yellow-500'} transition-all duration-500`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">
              {hasErrors
                ? 'An error occurred during processing'
                : 'Updated Automatically as the City reviews your submission'}
            </p>
          </div>

          {/* Stepper Header (Horizontal Tabs) */}
          <div className="flex gap-6 overflow-x-auto no-scrollbar mb-6 pb-2">
            {stepData.map((step, index) => {
              const isCompleted = step.status === 'completed';
              const isActive = index === activeStep;
              const isError = step.script_status === 'error';

              return (
                <div
                  key={step._id}
                  className={`flex flex-col items-center cursor-pointer flex-shrink-0`}
                  onClick={() => setActiveStep(index)}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-colors
                ${isCompleted ? (isError ? 'bg-red-500 text-white' : 'bg-yellow-500 text-black') : isActive ? 'bg-gray-600 text-white' : 'bg-gray-700 text-gray-400'}`}
                  >
                    {isCompleted ? <Check size={20} /> : step.step_number}
                  </div>
                  <span
                    className={`text-xs font-medium ${isActive ? 'text-white' : isError ? 'text-red-400' : 'text-gray-400'}`}
                  >
                    {step.step_name}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Screenshot / Content */}
          <div className="w-full flex justify-center mb-6">
            {stepData[activeStep]?.screenshot ? (
              <div
                onClick={() => handleViewScreenshot(stepData[activeStep].screenshot, stepData[activeStep].step_name)}
                className={`cursor-pointer max-w-3xl w-full h-96 bg-gray-800 rounded-lg overflow-hidden border border-gray-700 ${hasErrors ? 'hover:border-red-500' : 'hover:border-yellow-500'} transition-colors`}
              >
                <img
                  src={stepData[activeStep].screenshot}
                  alt={stepData[activeStep].step_name}
                  className="w-full h-full object-cover object-top opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
            ) : (
              <div className="text-gray-500">No screenshot available</div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between gap-4">
            <Button
              onClick={prevStep}
              className="px-4 py-2 bg-gray-700 text-gray-400 rounded disabled:opacity-50"
              disabled={activeStep === 0}
            >
              Previous
            </Button>
            <Button
              onClick={nextStep}
              className={`px-4 py-2 ${hasErrors ? 'bg-red-500 text-white' : 'bg-yellow-500 text-black'} rounded disabled:opacity-50`}
              disabled={activeStep === stepData.length - 1}
            >
              Next
            </Button>
          </div>

          {/* Screenshot Modal */}
          <Dialog open={!!selectedScreenshot} onOpenChange={() => setSelectedScreenshot(null)}>
            <DialogContent
              className="
            w-[95vw]
            max-w-5xl
            max-h-[90vh]
            bg-[#222]
            border-gray-800
            p-0
            overflow-hidden 
          "
            >
              <DialogHeader className="px-4 py-3 sm:p-6">
                <DialogTitle className="text-base sm:text-xl text-white">{selectedStepName}</DialogTitle>
              </DialogHeader>

              {/* Scrollable image container */}
              <div className="px-4 pb-4 sm:px-6 sm:pb-6">
                <div
                  className="
              bg-black
              rounded-lg
              overflow-y-auto
              max-h-[70vh]
              no-scrollbar  
            "
                >
                  <img src={selectedScreenshot} alt={selectedStepName} className="w-full h-auto" />
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default PermitStepper;
